// src/components/LayerTabs.tsx
import { memo } from 'react';
import { LayerName, LAYER_LABELS, LAYER_ORDER } from '../types/capsule';
import '../styles/LayerTabs.css';

interface LayerTabsProps {
  /** Available layers for this capsule */
  availableLayers: LayerName[];
  /** Currently selected layer */
  selectedLayer: LayerName | null;
  /** Callback when layer is selected */
  onSelectLayer: (layer: LayerName) => void;
  /** Whether audio is available for each layer */
  hasAudio?: Partial<Record<LayerName, boolean>>;
  /** Whether to show all layers (with unavailable ones grayed out) */
  showAllLayers?: boolean;
  /** Layout orientation */
  orientation?: 'horizontal' | 'vertical';
}

export const LayerTabs = memo(function LayerTabs({
  availableLayers,
  selectedLayer,
  onSelectLayer,
  hasAudio = {},
  showAllLayers = false,
  orientation = 'horizontal',
}: LayerTabsProps) {
  // Either show all layers or just available ones
  const layersToShow = showAllLayers
    ? LAYER_ORDER
    : LAYER_ORDER.filter(l => availableLayers.includes(l));

  return (
    <div className={`layer-tabs ${orientation === 'vertical' ? 'layer-tabs--vertical' : ''}`} role="tablist">
      {layersToShow.map((layer, index) => {
        const isAvailable = availableLayers.includes(layer);
        const isDisabled = !isAvailable;

        return (
          <button
            key={layer}
            role="tab"
            aria-selected={layer === selectedLayer}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            className={`layer-tab ${layer === selectedLayer ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
            onClick={() => isAvailable && onSelectLayer(layer)}
          >
            <span className="layer-tab-number">{index + 1}</span>
            <span className="layer-tab-label">{LAYER_LABELS[layer]}</span>
            {hasAudio[layer] && (
              <span className="layer-tab-audio-badge" title="Has audio recording">
                ♪
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
});
